// ============================================================
// fieldRefusals.ts — the server's refusal of ONE field in an autosave
// (a 400 whose body names `field`), shown under that field until it's edited
// again. Written by the autosave hooks (useAutoSave, useClientAutoSave,
// useCaseDeadlineAutoSave); read by the sections via useFieldRefusal.
//
// Why a separate channel (not the shared saveError): when one field is
// refused the hooks put it back to its saved value and LET THE OTHER CHANGES
// SAVE — so the save as a whole succeeds and saveError clears, but the
// person still needs to see why their date didn't stick.
//
// Scopes: caseScope(id) · clientScope(id) · deadlineScope(id).
// ============================================================
import { create } from 'zustand'

export const caseScope = (id: string): string => `case:${id}`
export const clientScope = (id: string): string => `client:${id}`
export const deadlineScope = (id: string): string => `deadline:${id}`

interface RefusalState {
  byKey: Record<string, string>
  setRefusal: (scope: string, field: string, message: string) => void
  clearRefusal: (scope: string, field: string) => void
}
const keyOf = (scope: string, field: string): string => `${scope}#${field}`

export const useFieldRefusalStore = create<RefusalState>((set) => ({
  byKey: {},
  setRefusal: (scope, field, message) => set((s) => ({ byKey: { ...s.byKey, [keyOf(scope, field)]: message } })),
  clearRefusal: (scope, field) => set((s) => {
    const k = keyOf(scope, field)
    if (!(k in s.byKey)) return s
    const next = { ...s.byKey }
    delete next[k]
    return { byKey: next }
  }),
}))

// The refusal message for `field` in `scope`, or null.
export const useFieldRefusal = (scope: string | null | undefined, field: string): string | null =>
  useFieldRefusalStore((s) => (scope ? s.byKey[keyOf(scope, field)] ?? null : null))

// A 400 that names ONE field → { field, message }, else null. (The server's
// `field` decides — never the message's wording.)
export const refusedField = (err: unknown): { field: string; message: string } | null => {
  const r = (err as { response?: { status?: number; data?: { field?: unknown; error?: unknown } } } | null)?.response
  if (!r || r.status !== 400 || typeof r.data?.field !== 'string' || !r.data.field) return null
  return { field: r.data.field, message: typeof r.data.error === 'string' ? r.data.error : 'This value wasn’t saved.' }
}

// Imperative read (after an await, where a hook value would be stale).
export const getFieldRefusal = (scope: string, field: string): string | null =>
  useFieldRefusalStore.getState().byKey[keyOf(scope, field)] ?? null
